import React from 'react';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subMonths } from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DateRange {
    startDate: Date;
    endDate: Date;
}

export interface QuickOption {
    labelKey: string;
    fallback: string;
    getRange: () => DateRange;
}

// ─── Quick-select data ────────────────────────────────────────────────────────

const startOfToday = (): Date => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

export const buildQuickOptions = (): QuickOption[] => [
    { labelKey: 'calendar.today',      fallback: 'Today',        getRange: () => { const today = startOfToday(); return { startDate: today, endDate: today }; } },
    { labelKey: 'calendar.yesterday',  fallback: 'Yesterday',    getRange: () => { const yesterday = subDays(startOfToday(), 1); return { startDate: yesterday, endDate: yesterday }; } },
    { labelKey: 'calendar.lastWeek',   fallback: 'Last week',    getRange: () => { const ref = subDays(startOfToday(), 7); return { startDate: startOfWeek(ref, { weekStartsOn: 1 }), endDate: endOfWeek(ref, { weekStartsOn: 1 }) }; } },
    { labelKey: 'calendar.lastMonth',  fallback: 'Last month',   getRange: () => { const p = subMonths(startOfToday(), 1); return { startDate: startOfMonth(p), endDate: endOfMonth(p) }; } },
    { labelKey: 'calendar.last7Days',  fallback: 'Last 7 days',  getRange: () => { const today = startOfToday(); return { startDate: subDays(today, 7), endDate: today }; } },
    { labelKey: 'calendar.last30Days', fallback: 'Last 30 days', getRange: () => { const today = startOfToday(); return { startDate: subDays(today, 30), endDate: today }; } },
];

export const QUICK_OPTIONS = buildQuickOptions();

export const matchQuickOption = (start: Date, end: Date): number =>
    QUICK_OPTIONS.findIndex(opt => {
        const r = opt.getRange();
        return r.startDate.getTime() === start.getTime() && r.endDate.getTime() === end.getTime();
    });

// ─── Component ────────────────────────────────────────────────────────────────

interface CalendarQuickSelectProps {
    activeQuickIdx: number;
    onSelect: (opt: QuickOption) => void;
    translate?: (key: string) => string;
}

export const CalendarQuickSelect: React.FC<CalendarQuickSelectProps> = ({
    activeQuickIdx,
    onSelect,
    translate,
}) => {
    const optionRefs = React.useRef<Array<HTMLLIElement | null>>([]);
    const t = (key: string, fallback: string) => translate?.(key) ?? fallback;

    const focusOption = (index: number) => {
        const normalized = (index + QUICK_OPTIONS.length) % QUICK_OPTIONS.length;
        optionRefs.current[normalized]?.focus();
    };

    const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, idx: number, opt: QuickOption) => {
        switch (event.key) {
            case 'ArrowDown':
            case 'Down':
                event.preventDefault();
                focusOption(idx + 1);
                break;
            case 'ArrowUp':
            case 'Up':
                event.preventDefault();
                focusOption(idx - 1);
                break;
            case 'Home':
                event.preventDefault();
                focusOption(0);
                break;
            case 'End':
                event.preventDefault();
                focusOption(QUICK_OPTIONS.length - 1);
                break;
            case 'Enter':
            case ' ':
            case 'Spacebar':
                event.preventDefault();
                onSelect(opt);
                break;
            default:
                break;
        }
    };

    return (
        <ul className="calendar-quick-select" role="listbox" aria-label={t('calendar.quickSelect', 'Quick select')}>
            {QUICK_OPTIONS.map((opt, idx) => (
                <li
                    key={opt.labelKey}
                    ref={element => {
                        optionRefs.current[idx] = element;
                    }}
                    role="option"
                    aria-selected={idx === activeQuickIdx}
                    tabIndex={idx === activeQuickIdx || (activeQuickIdx < 0 && idx === 0) ? 0 : -1}
                    className={`calendar-quick-option${idx === activeQuickIdx ? ' calendar-quick-option--active' : ''}`}
                    onClick={() => onSelect(opt)}
                    onKeyDown={event => handleOptionKeyDown(event, idx, opt)}
                >
                    {idx === activeQuickIdx && <span className="calendar-quick-check">✓</span>}
                    {t(opt.labelKey, opt.fallback)}
                </li>
            ))}
        </ul>
    );
};

export default CalendarQuickSelect;

